var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * The one recorder.
 *
 * Both the admin bench and a real interview drive this module; they differ only
 * in the `RecordingSink` they pass. Two independent recorder implementations is
 * exactly how the previous code drifted — the realtime context's retry queue was
 * unbounded while the cost-optimized one capped at 60, for the same feature.
 *
 * ADMIN CONFIGURATION IS A CEILING. Every rung of the fallback ladder is at or
 * below the configured profile. There is no path through this module that
 * records at higher quality than an admin asked for, whatever the device is
 * capable of. A 1080p60 machine given a 720p15 profile records 720p15.
 *
 * WHAT THIS FIXES: today a failed `MediaRecorder` constructor is caught, written
 * to `console.warn`, and the interview proceeds with no recording and no signal
 * to anyone. Here, exhausting the ladder is a reported terminal state.
 */
import { recordingCaptureConstraints, recordingRecorderOptions, } from '../context/recording-config';
import { AUTO_PREFERENCE, RECORDING_PROFILES, defaultSupportProbe, } from './profiles';
/** Default cadence at which MediaRecorder emits a Blob. Matches the value both
 *  interview contexts have used in production. */
export const DEFAULT_TIMESLICE_MS = 5000;
/**
 * The profile that reproduces today's production behaviour byte for byte:
 * a mimeType and nothing else.
 *
 * This exists so the contexts can adopt the shared recorder WITHOUT changing
 * what they record. Applying the admin ceiling is a separate, flag-gated step —
 * folding it into the structural refactor would make a behaviour change
 * indistinguishable from a plumbing change, and there would be no clean way to
 * tell which one broke a recording.
 *
 * `maxWidth`/`maxFrameRate` are 0 to say "impose no capture constraint", which
 * is precisely what `getDisplayMedia` is called with today.
 */
export const legacyProfile = () => ({
    mimeType: 'video/webm;codecs=vp8,opus',
    profileId: 'vp8-webm',
    extension: 'webm',
    maxWidth: 0,
    maxFrameRate: 0,
    rung: 1,
    label: 'legacy (unconstrained)',
});
const containerAllowed = (profileId, transport) => transport === 's3-multipart' || RECORDING_PROFILES[profileId].container === 'webm';
/** Order the ladder walks: the configured profile first, then the rest. */
const orderedProfileIds = (configured, transport) => {
    const preferred = configured === 'auto' ? AUTO_PREFERENCE : [configured, ...AUTO_PREFERENCE];
    const seen = new Set();
    return preferred.filter((id) => {
        if (seen.has(id))
            return false;
        seen.add(id);
        return containerAllowed(id, transport);
    });
};
/**
 * The deterministic ladder.
 *
 * Ordered most- to least-preferred. EVERY rung is <= the configured ceiling:
 * width and both bitrates are copied unchanged from the config at every rung,
 * and the only rung that moves frame rate moves it DOWN. Changing codec never
 * changes a quality ceiling — that separation is the point, because a codec
 * fallback that quietly raised the bitrate would defeat the compression work
 * this whole feature sits on top of.
 */
export const buildLadder = (cfg, transport = 'legacy-websocket') => {
    const audio = cfg.audio_enabled;
    const base = recordingRecorderOptions(cfg, '');
    const v = base.videoBitsPerSecond;
    const a = base.audioBitsPerSecond;
    const ceiling = {
        videoBitsPerSecond: v,
        audioBitsPerSecond: a,
        maxWidth: cfg.max_width,
        maxFrameRate: cfg.max_frame_rate,
    };
    const rungs = [];
    for (const id of orderedProfileIds(cfg.encoding_profile, transport)) {
        const p = RECORDING_PROFILES[id];
        for (const mimeType of p.mimeCandidates(audio)) {
            rungs.push(Object.assign(Object.assign({}, ceiling), { mimeType, profileId: id, extension: p.extension, label: `${p.label} @ ${cfg.max_width}px/${cfg.max_frame_rate}fps` }));
        }
    }
    // Last resorts, both still under every configured ceiling: halve the frame
    // rate, then let the container pick its own codec.
    const halved = Math.max(1, Math.floor(cfg.max_frame_rate / 2));
    const baseline = RECORDING_PROFILES['vp8-webm'];
    rungs.push(Object.assign(Object.assign({}, ceiling), { maxFrameRate: halved, mimeType: baseline.mimeCandidates(audio)[0], profileId: 'vp8-webm', extension: 'webm', label: `VP8 / WebM @ ${cfg.max_width}px/${halved}fps (reduced)` }));
    rungs.push(Object.assign(Object.assign({}, ceiling), { maxFrameRate: halved, mimeType: 'video/webm', profileId: 'vp8-webm', extension: 'webm', label: `container default @ ${cfg.max_width}px/${halved}fps` }));
    return rungs.map((r, i) => (Object.assign(Object.assign({}, r), { rung: i + 1 })));
};
/**
 * Pick the highest supported rung.
 *
 * Under `strict`, only rung 1 is acceptable: a strict deployment would rather
 * record nothing than record something other than what was specified. Under
 * `best_effort` (the default) we walk down. Neither ever walks up.
 */
export const selectProfile = (cfg, probe = defaultSupportProbe, transport = 'legacy-websocket') => {
    var _a;
    const ladder = buildLadder(cfg, transport);
    const attempted = [];
    const strict = cfg.fallback_policy === 'strict';
    for (let i = 0; i < ladder.length; i++) {
        const p = ladder[i];
        const supported = probe(p.mimeType);
        attempted.push({ mimeType: p.mimeType, supported, rung: p.rung });
        if (supported)
            return { profile: p, attempted };
        // Strict refuses to deviate from the configured PROFILE — but a profile can
        // offer several MIME spellings of itself (MP4 with AAC, then with Opus),
        // and exhausting those is not deviation. Only leaving the profile is.
        if (strict && ((_a = ladder[i + 1]) === null || _a === void 0 ? void 0 : _a.profileId) !== p.profileId) {
            return {
                profile: null,
                attempted,
                reason: `strict policy: configured profile (${p.mimeType}) is unsupported`,
            };
        }
    }
    return { profile: null, attempted, reason: 'no supported codec on the fallback ladder' };
};
/** Capture constraints for the display track, capped by the selected rung. */
export const captureConstraintsFor = (profile) => ({
    frameRate: { max: profile.maxFrameRate },
    width: { max: profile.maxWidth },
});
/** Same, straight from config — used before a profile is chosen. */
export const captureConstraintsForConfig = recordingCaptureConstraints;
/**
 * Drives one MediaRecorder over one stream into one sink.
 *
 * `pause()` / `resume()` are exposed because the interview sink applies
 * backpressure with them: when the upload buffer hits its hard ceiling the only
 * safe move is to stop generating data. Dropping a buffered part is never an
 * option — a missing middle part corrupts every byte after it.
 */
export class SharedRecorder {
    constructor(sink, profile, events = {}, timesliceMs = DEFAULT_TIMESLICE_MS) {
        this.sink = sink;
        this.profile = profile;
        this.events = events;
        this.timesliceMs = timesliceMs;
        this.recorder = null;
        this.index = 0;
        this._state = 'idle';
        this.startedAtMs = null;
        this.pausedTotalMs = 0;
        this.pausedAtMs = null;
    }
    get state() {
        return this._state;
    }
    /**
     * The underlying `MediaRecorder`.
     *
     * Exposed for the teardown paths that legitimately need the raw object: the
     * unload handler temporarily swaps `ondataavailable` so the final chunk can
     * be beaconed, and the flush path calls `requestData()`. Those are delicate,
     * well-tested, truncation-critical routines, and rewriting them was not worth
     * the risk to make the encapsulation look tidier.
     *
     * What matters is that CONSTRUCTION, options and chunk routing live here and
     * nowhere else — there is no second `new MediaRecorder` in the codebase.
     */
    get mediaRecorder() {
        return this.recorder;
    }
    /** The profile this recorder was constructed with — the rung actually in use
     *  after the capability ladder was walked. */
    get profileInUse() {
        return this.profile;
    }
    /**
     * Monotonic milliseconds, for measuring ELAPSED time only.
     *
     * VA-28265 Phase 2.1. `Date.now()` is the wall clock and can move: an NTP
     * step or a user changing the system clock mid-interview shifts it, and every
     * proctoring frame, endpoint event and transcript row stamped afterwards
     * inherits the jump. That is not hypothetical here — `proctoring/timeline.py`
     * documents a live case (interview-d7fae649) where `t` leapt 19.708 -> 136.872
     * inside a session whose segment is 19.826 s long, and everything after it
     * vanished from the report.
     *
     * `performance.now()` is monotonic and immune. It is relative to page load
     * rather than the epoch, which is exactly right for a duration and useless for
     * a date — so it is used ONLY here. Absolute wall-clock timestamps elsewhere
     * (`focus.durationSec`, session starts, anything a human reads as a time of
     * day) deliberately keep `Date.now()`.
     *
     * Falls back where `performance` is absent so a stale environment degrades to
     * today's behaviour rather than to zero.
     */
    nowMs() {
        if (typeof performance !== 'undefined' && typeof performance.now === 'function') {
            return performance.now();
        }
        return Date.now();
    }
    /** Seconds of actual recording, excluding paused time. This — not
     *  `HTMLVideoElement.duration` — is the trustworthy duration, because a
     *  concatenated MediaRecorder WebM reports `Infinity`.
     *
     *  Measured on a monotonic clock (see `nowMs`), so it cannot be moved by a
     *  system clock change. */
    get elapsedSec() {
        if (this.startedAtMs === null)
            return 0;
        const now = this.nowMs();
        const paused = this.pausedTotalMs + (this.pausedAtMs !== null ? now - this.pausedAtMs : 0);
        return Math.max(0, (now - this.startedAtMs - paused) / 1000);
    }
    setState(s) {
        var _a, _b;
        this._state = s;
        (_b = (_a = this.events).onStateChange) === null || _b === void 0 ? void 0 : _b.call(_a, s);
    }
    start(stream) {
        var _a, _b, _c, _d, _e, _f;
        if (this.recorder)
            return true;
        // Only defined options are passed. Under the LEGACY profile that leaves
        // `{ mimeType }` alone — exactly the object today's contexts construct — so
        // adopting this module cannot change what the encoder does.
        const opts = { mimeType: this.profile.mimeType };
        if (this.profile.videoBitsPerSecond !== undefined) {
            opts.videoBitsPerSecond = this.profile.videoBitsPerSecond;
        }
        if (this.profile.audioBitsPerSecond !== undefined) {
            opts.audioBitsPerSecond = this.profile.audioBitsPerSecond;
        }
        try {
            this.recorder = new MediaRecorder(stream, opts);
        }
        catch (err) {
            this.setState('failed');
            (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, 'MediaRecorder could not be constructed', err);
            return false;
        }
        this.recorder.ondataavailable = (e) => {
            if (!e.data || e.data.size === 0)
                return;
            const i = this.index++;
            // Sinks serialise internally; a rejection here must not kill the
            // recorder, but it must be reported rather than swallowed.
            void this.sink.accept(e.data, i).catch((err) => {
                var _a, _b;
                (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, `sink rejected chunk #${i}`, err);
            });
        };
        this.recorder.onerror = (e) => {
            var _a, _b;
            this.setState('failed');
            (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, 'MediaRecorder error', e);
        };
        try {
            this.recorder.start(this.timesliceMs);
        }
        catch (err) {
            this.setState('failed');
            (_d = (_c = this.events).onFailure) === null || _d === void 0 ? void 0 : _d.call(_c, 'MediaRecorder.start failed', err);
            return false;
        }
        // Same clock as `elapsedSec` reads — mixing the two would make the
        // subtraction meaningless.
        this.startedAtMs = this.nowMs();
        this.setState('recording');
        if (this.profile.rung > 1) {
            (_f = (_e = this.events).onDegraded) === null || _f === void 0 ? void 0 : _f.call(_e, `recording at fallback rung ${this.profile.rung}: ${this.profile.label}`);
        }
        return true;
    }
    /** Backpressure. Safe to call when already paused. */
    pause() {
        var _a, _b;
        if (!this.recorder || this._state !== 'recording')
            return;
        try {
            this.recorder.pause();
            this.pausedAtMs = this.nowMs();
            this.setState('paused');
        }
        catch (err) {
            (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, 'MediaRecorder.pause failed', err);
        }
    }
    resume() {
        var _a, _b;
        if (!this.recorder || this._state !== 'paused')
            return;
        try {
            this.recorder.resume();
            if (this.pausedAtMs !== null) {
                this.pausedTotalMs += this.nowMs() - this.pausedAtMs;
                this.pausedAtMs = null;
            }
            this.setState('recording');
        }
        catch (err) {
            (_b = (_a = this.events).onFailure) === null || _b === void 0 ? void 0 : _b.call(_a, 'MediaRecorder.resume failed', err);
        }
    }
    /**
     * Stop and wait for the browser to flush its final Blob.
     *
     * `ondataavailable` fires before `onstop` per spec, so awaiting `onstop` is
     * what guarantees the tail is captured. Skipping this wait is how a recording
     * loses its last few seconds.
     */
    stop() {
        return __awaiter(this, void 0, void 0, function* () {
            const rec = this.recorder;
            if (!rec || this._state === 'stopped')
                return;
            yield new Promise((resolve) => {
                const done = () => resolve();
                rec.onstop = done;
                try {
                    if (rec.state !== 'inactive')
                        rec.stop();
                    else
                        done();
                }
                catch (_a) {
                    done();
                }
                // Never hang a teardown on a recorder that will not fire onstop.
                setTimeout(done, 10000);
            });
            this.setState('stopped');
            yield this.sink.finalize();
        });
    }
}
